/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-unused-vars */
import axios from 'axios';
import map from 'lodash-es/map';
import size from 'lodash-es/size';
import {
  EditRequest,
  User,
  UsersResponse,
  ChangeRoleRequest,
  SigninResponse,
  Tokens,
  TagsResponse,
  SignupRequest,
  SigninRequest,
  UsersRequest,
  UpdateUserRequest,
  VerdictRequest,
  OkResponse,
  OnlyId,
  EditTagRequest,
  UpdateAvatarRequest,
  UploadRequest,
  UploadResponse,
  ResourceRequest,
  ResourcesPage,
  Resource,
  LikeRequest,
  Tags,
  TagsPage,
  TagsRequest,
  MatchedRequest,
} from 'store/types';
import reduce from 'lodash-es/reduce';

export const BASE_URL =
  location.hostname === 'localhost' ? 'https://visualstock.ru/api' : '/api';
export let currentAuthHeader = '';

axios.defaults.baseURL = BASE_URL;

axios.interceptors.response.use(function (response) {
  // console.log(response);
  return response;
});

export const setToken = (token: string) => {
  if (token) {
    currentAuthHeader = `Bearer ${token}`;
    axios.defaults.headers.common['Authorization'] = currentAuthHeader;
  }
};

export const deleteToken = () => {
  currentAuthHeader = '';
  delete axios.defaults.headers.common['Authorization'];
};

export const authApi = {
  getUser: async () => {
    const response = await axios.get<User>('/user/self');
    return response.data;
  },
  signin: async (arg: SigninRequest) => {
    const response = await axios.post<SigninResponse>('/auth/', arg);
    return response.data;
  },
  signup: async (arg: SignupRequest) => {
    const response = await axios.post<User>('/user/create', arg);
    return response.data;
  },
  refreshToken: async () => {
    const body = {
      refresh_token:
        localStorage.getItem('refreshToken') ||
        sessionStorage.getItem('refreshToken'),
    };

    const response = await axios.post<Tokens>('/auth/refresh', body);
    return response.data;
  },

  updateSelf: async (arg: UpdateUserRequest) => {
    const response = await axios.patch<User>('/user/self', arg);
    return response.data;
  },

  updateAvatar: async (arg: UpdateAvatarRequest) => {
    const response = await axios.post<User>(
      `/user/avatar/${arg._id}`,
      arg.avatar,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },
};

type RecoverParams = {
  step: string;
  fields?: {
    [x: string]: string;
  };
};
export type ChangeParams = {
  old_password: string;
  new_password: string;
};

export const passwordAPI = {
  recover: async (args: RecoverParams) => {
    const response = await axios.post<OkResponse & { error?: string }>(
      `/user/password/recover?step=${Number(args.step) + 1}`,
      args.fields
    );

    if (response.data.ok) {
      return 'success';
    } else if (response.data.error === 'Incorrect code') {
      return 'error_incorrect_code';
    }
  },
  change: async (args: ChangeParams) => {
    const response = await axios.post<boolean>('/user/password/change', args);

    return response.data;
  },
  checkOld: async (val: string) => {
    const response = await axios.post<boolean>('/user/password/check', {
      password: val,
    });

    return response.data;
  },
};

export const resourcesAPI = {
  getResourceList: async (arg: ResourceRequest) => {
    const tags = arg.tags ? `&tags=${arg.tags}` : '';
    const special = arg.special
      ? `&${arg.special.name}=${arg.special.value}`
      : '';
    const category = arg.category ? `&category=${arg.category}` : '';
    const sort = `&sort=${arg.sort}`;

    const url = `/resource/?offset=${arg.offset}&limit=${arg.limit}${category}${special}${tags}${sort}`;

    const response = await axios.get<Omit<ResourcesPage, 'url' | 'name'>>(url);
    return {
      ...response.data,
      requestMeta: arg,
      responseTotal: size(response.data.data),
    } as ResourcesPage;
  },

  edit: async (arg: EditRequest) => {
    const params: { tags: string[] } = {
      tags: [],
    };
    params.tags = map(arg.params.tags, (tag) => tag.name);

    const response = await axios.patch<Resource>(`/resource/${arg.id}`, params);
    return response.data;
  },

  like: async (arg: LikeRequest) => {
    const response = await axios.post<Resource>(`/resource/like/${arg._id}`);
    return response.data;
  },

  getById: async (arg: OnlyId) => {
    const response = await axios.get<Resource>(`/resource/${arg._id}`);
    return response.data;
  },

  delete: async (arg: OnlyId) => {
    const response = await axios.delete<OkResponse>(`/resource/${arg._id}`);
    return response.data;
  },

  upload: async (arg: UploadRequest) => {
    const response = await axios.post<UploadResponse>(
      '/resource/create',
      arg.formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },
};

export const adminAPI = {
  getUsers: async (arg: UsersRequest) => {
    const inactive = arg.type === 'existing' ? 'false' : 'true';
    const response = await axios.get<UsersResponse>(
      `/user/?inactive=${inactive}&sort=-role,${arg.sort}&offset=${arg.offset}&limit=${arg.limit}`
    );

    return response.data;
  },
  deleteUser: async (arg: OnlyId) => {
    const response = await axios.delete<OkResponse>(`/user/${arg._id}`);
    return response.data;
  },
  activateUser: async (arg: VerdictRequest) => {
    const response = await axios.patch<User>(`/user/${arg._id}`, {
      is_active: true,
    });
    return response.data;
  },
  changeRole: async (arg: ChangeRoleRequest) => {
    const response = await axios.patch<User>(`/user/${arg._id}`, {
      role: arg.role,
    });
    return response.data;
  },
};

export const deleteAvatar = async (arg: OnlyId) => {
  const response = await axios.delete<User>(`/user/avatar/${arg._id}`);
  return response.data;
};

export const tagsAPI = {
  fetchMatched: async (arg: MatchedRequest) => {
    const response = await axios.get<Tags>(
      `/resource/search_tags?tag=${arg.search}&position=${arg.position}`
    );
    return response.data;
  },

  fetchStartTags: async (tagsString: string) => {
    const response = await axios.get<TagsResponse>(
      `/resourcetags/validate?tags=${tagsString}`
    );
    return response.data;
  },

  getAllTags: async (arg: TagsRequest) => {
    const response = await axios.get<TagsPage>(`/resource/tags`);
    return response.data;
  },

  getTopTags: async () => {
    const response = await axios.get<TagsPage>(
      `/resource/tags?offset=0&limit=10`
    );
    return response.data;
  },

  editTag: async (arg: EditTagRequest) => {
    const response = await axios.post<Tags>(
      `/resource/tags/merge?old_tag=${arg.old.name}&new_tags=${map(
        arg.new,
        (tag) => tag.name
      )}`
    );
    return response.data;
  },
};
